import {
  EyeOutlined,
  RedoOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, Form, Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useLazySearchMedicalDocumentsQuery,
  useSendToMedFlexMutation,
  useUpdateMedicalDocumentsStatusesMutation,
} from '@app/api/MedicalDocuments';
import type {
  ModelMedicalDocumentResponse,
  SearchMedicalDocumentsRequest,
} from '@app/api/MedicalDocuments';
import { CDA_4_KIND } from '@app/components/RecordModal/components/MedicalCard/data';
import { SUCCESS } from '@app/constants';
import { useMedicalDocumentsAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useRegistryFilterState } from '@app/shared/hooks';

import {
  DocumentStatus,
  getDocumentStatusLabel,
  statusColors,
  type FiltersFormValues,
  type MedicalDocumentTableItem,
  type UseMedicalCardRegistryProps,
} from '../types';

import type { BaseOptionType } from 'antd/es/select';
import type { ColumnsType } from 'antd/es/table';

const INITIAL_PAGE = 1;
const INITIAL_PAGE_SIZE = 20;

// TODO(egisz-stage4, B12): поллинг вместо push-события. Как только бэк даст
// SignalR-событие смены статуса — заменить интервал на подписку.
const STATUS_POLL_INTERVAL_MS = 60000;

// Статусы, при которых документ ещё «в пути» через Медфлекс и статус может
// смениться на сервере без действий пользователя.
const PENDING_SYNC_STATUSES: DocumentStatus[] = [
  DocumentStatus.SENT,
  DocumentStatus.IN_QUEUE,
  DocumentStatus.SENT_TO_REGISTRY,
];

export const useMedicalCardRegistry = ({
  tableData,
  setTableData,
  setTotalRows,
}: UseMedicalCardRegistryProps) => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { hasUserRightToSendMedicalDocuments } =
    useMedicalDocumentsAccessRights();

  // Выбранные фильтры переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161): хранилище вкладочное, как у реестра
  // записей и реестра задач.
  const [selectedStatuses, setSelectedStatuses] = useRegistryFilterState<
    BaseOptionType[]
  >('medical-cards', 'selectedStatuses', []);

  const [selectedDoctors, setSelectedDoctors] = useRegistryFilterState<
    BaseOptionType[]
  >('medical-cards', 'selectedDoctors', []);

  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE);

  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_PAGE_SIZE);

  const [viewDocumentId, setViewDocumentId] = useState<number | null>(null);

  const { showNotification } = useNotification();

  const [form] = Form.useForm();

  const [
    searchMedicalDocuments,
    { isLoading: isSearchLoading, error: searchError },
  ] = useLazySearchMedicalDocumentsQuery();
  const [sendToMedFlex, { isLoading: isSendToMedFlexLoading }] =
    useSendToMedFlexMutation();
  const [updateDocumentsStatuses, { isLoading: isUpdatingStatuses }] =
    useUpdateMedicalDocumentsStatusesMutation();

  // Всегда указывает на актуальный handleRefreshStatuses, чтобы поллинг и
  // обработчик отправки не тянули устаревшее замыкание фильтров/страницы.
  const refreshStatusesRef = useRef<() => Promise<void>>(() =>
    Promise.resolve()
  );

  useEffect(() => {
    if (searchError) {
      showNotification(handleCatchError(searchError));
    }
  }, [searchError, showNotification]);

  const renderStatus = (
    status: DocumentStatus,
    record: MedicalDocumentTableItem
  ): React.ReactNode => {
    const errors = record.Errors ?? [];
    const hasErrors = errors.length > 0;

    return (
      <Space size={6}>
        <Tag color={statusColors[status]}>{getDocumentStatusLabel(status)}</Tag>

        {hasErrors && (
          <Tooltip
            title={
              <div>
                {errors.map((err, i) => (
                  <div key={i}>
                    {err.code ? `${err.code}: ` : ''}
                    {err.message}
                  </div>
                ))}
              </div>
            }
          >
            <ExclamationCircleOutlined
              style={{ color: '#ff4d4f', cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  const renderKind = (kind: number): React.ReactNode => {
    const kindMap: Record<number, string> = {
      [CDA_4_KIND]: 'Протокол консультации (CDA) Редакция 4',
    };

    return kindMap[kind] || `Тип ${kind}`;
  };

  const renderDate = (date: string | null): React.ReactNode =>
    date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-';

  const handleSendToMedFlex = useCallback(
    async (record: MedicalDocumentTableItem) => {
      try {
        const id = record.Id;

        await sendToMedFlex({
          ActiveBranchId,
          id,
        }).unwrap();

        showNotification(
          'Медицинская карта успешно отправлена в MedFlex',
          SUCCESS
        );

        // После отправки статус на сервере меняется (SENT и далее) — сразу
        // синхронизируем и перечитываем список, чтобы он не «замирал».
        await refreshStatusesRef.current();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, sendToMedFlex, showNotification]
  );

  const openViewDocument = useCallback((id: number) => {
    setViewDocumentId(id);
  }, []);

  const closeViewDocument = useCallback(() => {
    setViewDocumentId(null);
  }, []);

  const columns = useMemo<ColumnsType<MedicalDocumentTableItem>>(
    () => [
      {
        title: 'Номер документа',
        dataIndex: 'DocumentNumber',
        key: 'DocumentNumber',
        width: 150,
      },
      {
        title: 'Пациент',
        dataIndex: 'PatientName',
        key: 'PatientName',
        width: 200,
      },
      {
        title: 'Врач',
        dataIndex: 'ResponsibleDoctorName',
        key: 'ResponsibleDoctorName',
        width: 200,
      },
      {
        title: 'Статус',
        dataIndex: 'Status',
        key: 'Status',
        width: 150,
        render: renderStatus,
      },
      {
        title: 'Тип документа',
        dataIndex: 'Kind',
        key: 'Kind',
        width: 130,
        render: renderKind,
      },
      {
        title: 'Дата создания',
        dataIndex: 'CreationDateTime',
        key: 'CreationDateTime',
        width: 180,
        render: renderDate,
      },
      {
        key: 'actions',
        width: 96,
        render: (_, record: MedicalDocumentTableItem) => {
          // ERROR — регистрация в РЭМД не прошла; отправку можно повторить
          // тем же SendDocument, что и для подписанного документа.
          const isSent = record.Status === DocumentStatus.SIGNED;
          const isFailed = record.Status === DocumentStatus.ERROR;
          const canSend =
            (isSent || isFailed) && hasUserRightToSendMedicalDocuments;

          return (
            <Space size={4}>
              <Tooltip title="Просмотр">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => openViewDocument(record.Id)}
                />
              </Tooltip>

              {canSend && (
                <Tooltip
                  title={
                    isFailed
                      ? 'Повторить отправку в МедФлекс'
                      : 'Отправить в МедФлекс'
                  }
                >
                  <Button
                    icon={isFailed ? <RedoOutlined /> : <SendOutlined />}
                    onClick={() => handleSendToMedFlex(record)}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [handleSendToMedFlex, openViewDocument, hasUserRightToSendMedicalDocuments]
  );

  const buildSearchPayload = (
    values?: FiltersFormValues
  ): Partial<SearchMedicalDocumentsRequest> => {
    return {
      Statuses: selectedStatuses?.map((item) => item.value) || null,
      DoctorId: Number(selectedDoctors?.[0]?.value) || null,
      DateFrom:
        values?.CreationDateRange?.[0]?.startOf('day').toISOString() || null,
      DateTo:
        values?.CreationDateRange?.[1]?.endOf('day').toISOString() || null,
      DoctorSignedDateFrom:
        values?.DoctorSignedDateRange?.[0]?.startOf('day').toISOString() ||
        null,
      DoctorSignedDateTo:
        values?.DoctorSignedDateRange?.[1]?.endOf('day').toISOString() || null,
      ChiefSignedDateFrom:
        values?.ChiefSignedDateRange?.[0]?.startOf('day').toISOString() || null,
      ChiefSignedDateTo:
        values?.ChiefSignedDateRange?.[1]?.endOf('day').toISOString() || null,
    };
  };

  const fetchMedicalDocuments = async (
    payload?: Partial<SearchMedicalDocumentsRequest>
  ) => {
    try {
      if (!ActiveBranchId) {
        return;
      }

      const response = await searchMedicalDocuments({
        ActiveBranchId,
        PatientId: null,
        DoctorId: null,
        Statuses: null,
        Kind: CDA_4_KIND,
        IsSigned: false,
        SignedByUserId: null,
        DateFrom: null,
        DateTo: null,
        DoctorSignedDateFrom: null,
        DoctorSignedDateTo: null,
        ChiefSignedDateFrom: null,
        ChiefSignedDateTo: null,
        SearchTerm: null,
        Page: pageNumber,
        PageSize: rowsPerPage,
        ...payload,
      }).unwrap();

      setTableData(response.Items || []);
      setTotalRows(response.Total || 0);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const refetchCurrent = async () => {
    const values = form.getFieldsValue() as FiltersFormValues;

    await fetchMedicalDocuments({
      ...buildSearchPayload(values),
      Page: pageNumber,
      PageSize: rowsPerPage,
    });
  };

  const handleRefreshStatuses = async () => {
    try {
      await updateDocumentsStatuses({ ActiveBranchId }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error));

      return;
    }

    await refetchCurrent();
  };

  useEffect(() => {
    refreshStatusesRef.current = handleRefreshStatuses;
  });

  const hasPendingSyncDocuments = tableData.some((document) =>
    PENDING_SYNC_STATUSES.includes(document.Status as DocumentStatus)
  );

  useEffect(() => {
    if (!hasPendingSyncDocuments) {
      return;
    }

    const intervalId = setInterval(() => {
      void refreshStatusesRef.current();
    }, STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [hasPendingSyncDocuments]);

  const submitFormHandler = async (values: FiltersFormValues) => {
    setPageNumber(INITIAL_PAGE);

    await fetchMedicalDocuments({
      ...buildSearchPayload(values),
      Page: INITIAL_PAGE,
      PageSize: rowsPerPage,
    });
  };

  const handleReset = async () => {
    form.resetFields();

    setSelectedStatuses([]);
    setSelectedDoctors([]);
    setPageNumber(INITIAL_PAGE);
    setRowsPerPage(INITIAL_PAGE_SIZE);

    await fetchMedicalDocuments({
      Statuses: null,
      DoctorId: null,
      DateFrom: null,
      DateTo: null,
      DoctorSignedDateFrom: null,
      DoctorSignedDateTo: null,
      ChiefSignedDateFrom: null,
      ChiefSignedDateTo: null,
      Page: INITIAL_PAGE,
      PageSize: INITIAL_PAGE_SIZE,
    });
  };

  const mapMedicalDocumentToTable = (
    item: ModelMedicalDocumentResponse
  ): MedicalDocumentTableItem => {
    return {
      Id: item.Id,
      DocumentNumber: item.DocumentNumber || '',
      PatientName: item.PatientName || '-',
      ResponsibleDoctorName: item.ResponsibleDoctorName || '-',
      Status: item.Status || '',
      Kind: item.Kind,
      CreationDateTime: item.CreationDateTime || '',
      Errors: item.Errors,
    };
  };

  const handleTableChange = async (page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);

    const values = form.getFieldsValue() as FiltersFormValues;

    await fetchMedicalDocuments({
      ...buildSearchPayload(values),
      Page: page,
      PageSize: pageSize,
    });
  };

  return {
    columns,
    filtersForm: form,
    submitFormHandler,
    handleReset,
    selectedStatuses,
    setSelectedStatuses,
    selectedDoctors,
    setSelectedDoctors,
    mapMedicalDocumentToTable,
    pageNumber,
    rowsPerPage,
    handleTableChange,
    tableData,
    isSearchLoading,
    isSendToMedFlexLoading,
    viewDocumentId,
    closeViewDocument,
    handleRefreshStatuses,
    isUpdatingStatuses,
  };
};
